import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

export function AgentConfigCreationPathChooser({
  onSelectManual,
  onSelectAi,
  saving,
}) {
  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 680, py: 4, px: 3, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "text.primary",
            mb: 1,
          }}
        >
          How should we configure this agent?
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary", mb: 3 }}>
          Choose whether you want to configure everything yourself or have AI
          configure itself from your natural-language requirements.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onSelectManual}
            disabled={saving}
            sx={creationPathButtonSx}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Configure manually
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
              >
                Use the full configuration UI for name, prompt, tools,
                knowledge, and policies.
              </Typography>
            </Box>
          </Button>
          <Button
            variant="outlined"
            onClick={onSelectAi}
            disabled={saving}
            sx={creationPathButtonSx}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                Let AI self-configure
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
              >
                Describe your requirements in natural language. The agent will
                be created with self-configuration awareness and edit enabled.
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function AgentConfigAiCreationPanel({
  aiRequirements,
  setAiRequirements,
  creationError,
  setCreationError,
  saving,
  stageLabel,
}) {
  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 680, py: 3, px: 3, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "text.primary",
            mb: 1,
          }}
        >
          Describe your instructions and requirements
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}>
          We will create the agent with self-configuration awareness and edit
          enabled, then automatically start the first chat with this message.
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={8}
          value={aiRequirements}
          onChange={(e) => {
            setAiRequirements(e.target.value);
            if (creationError) setCreationError("");
          }}
          placeholder="Example: Create an operations assistant that triages support requests, updates task status, and asks clarifying questions before escalating."
          disabled={saving}
        />
        {creationError && (
          <Typography sx={{ fontSize: "12px", color: "error.main", mt: 1.5 }}>
            {creationError}
          </Typography>
        )}
        {saving && (
          <Box
            sx={{
              mt: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              py: 1,
              borderRadius: "8px",
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CircularProgress size={16} sx={{ color: "text.secondary" }} />
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              {stageLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const creationPathButtonSx = {
  justifyContent: "flex-start",
  alignItems: "flex-start",
  textAlign: "left",
  textTransform: "none",
  borderRadius: "10px",
  borderColor: "divider",
  color: "text.primary",
  p: 2,
};
